import React, { useState } from 'react';
import { AppLayout } from '@/components/AppLayout';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, FilePlus, Users, Package, Settings,
  Download, ChevronDown, ChevronRight, FileText, Shield,
  BookOpen, CheckCircle, AlertTriangle, Info, HelpCircle,
  Printer, Database, LogOut, Search, Tag, Edit3, Trash2,
  Eye, PlusCircle, RefreshCcw, ToggleRight, ArrowRight,
  Star
} from 'lucide-react';

// ── Tipos ─────────────────────────────────────────────────────────────────────
interface Step {
  icon?: React.ReactNode;
  text: string;
  note?: string;
}

interface Section {
  id: string;
  icon: React.ReactNode;
  title: string;
  color: string;
  intro: string;
  steps?: Step[];
  subsections?: { title: string; steps: Step[] }[];
  tips?: string[];
  warnings?: string[];
}

// ── Datos del manual ──────────────────────────────────────────────────────────
const SECTIONS: Section[] = [
  {
    id: 'inicio',
    icon: <Shield size={20} />,
    title: 'Acceso y sesión',
    color: 'text-primary',
    intro: 'La aplicación está protegida con contraseña para garantizar que solo tú puedes acceder a los datos de tu empresa y clientes.',
    steps: [
      { icon: <ArrowRight size={14} />, text: 'Abre la aplicación en tu navegador. Verás la pantalla de inicio de sesión con el nombre de tu empresa.' },
      { icon: <ArrowRight size={14} />, text: 'Introduce la contraseña de acceso en el campo correspondiente.' },
      { icon: <CheckCircle size={14} />, text: 'Pulsa "Entrar" o presiona Enter. Accederás al panel principal.', note: 'La sesión dura 8 horas. Pasado ese tiempo, tendrás que volver a introducir la contraseña.' },
      { icon: <LogOut size={14} />, text: 'Para cerrar sesión manualmente, pulsa el icono de salida (⊗) en la esquina superior derecha del menú de navegación.' },
    ],
    tips: [
      'Si cierras el navegador, la sesión se cierra automáticamente por seguridad.',
      'Puedes cambiar la contraseña desde Configuración cuando lo necesites.',
    ],
  },
  {
    id: 'dashboard',
    icon: <LayoutDashboard size={20} />,
    title: 'Panel principal (Dashboard)',
    color: 'text-primary',
    intro: 'El Dashboard es la pantalla de inicio donde ves de un vistazo el estado de todos tus presupuestos y accesos rápidos a cada módulo.',
    steps: [
      { icon: <Info size={14} />, text: 'Los 4 indicadores superiores (KPIs) muestran: Total de presupuestos creados, Enviados a clientes, Aceptados y Borradores pendientes.' },
      { icon: <Info size={14} />, text: 'Debajo verás accesos directos a Clientes y Productos/Servicios.' },
      { icon: <FileText size={14} />, text: 'La tabla inferior lista todos los presupuestos. Puedes buscarlos por número, cliente o estado usando el buscador.' },
      { icon: <Edit3 size={14} />, text: 'Cada presupuesto tiene un menú de acciones (···): Editar, Ver PDF, Duplicar, Cambiar estado y Eliminar.' },
    ],
    tips: [
      'Pulsa "Nuevo presupuesto" (botón verde) para crear uno desde cero.',
      'Usa el filtro de estados para ver solo Borradores, Enviados, Aceptados o Rechazados.',
      'Al duplicar un presupuesto se crea una copia con nuevo número y estado "Borrador".',
    ],
  },
  {
    id: 'presupuestos',
    icon: <FilePlus size={20} />,
    title: 'Crear y editar presupuestos',
    color: 'text-primary',
    intro: 'El editor de presupuestos es el corazón de la aplicación. Desde aquí construyes el presupuesto línea a línea y lo personalizas para cada cliente o aseguradora.',
    subsections: [
      {
        title: 'Datos generales del presupuesto',
        steps: [
          { icon: <ArrowRight size={14} />, text: 'El número se genera automáticamente con el formato PRE-AÑO-NNN (ej: PRE-2026-001). No lo edites manualmente.' },
          { icon: <ArrowRight size={14} />, text: 'La fecha de emisión se rellena con el día actual. Puedes modificarla si necesitas una fecha diferente.' },
          { icon: <ArrowRight size={14} />, text: 'La "Fecha de validez" indica hasta cuándo es válida la oferta. Por defecto, 30 días desde la emisión.' },
          { icon: <ArrowRight size={14} />, text: 'El estado del presupuesto puede ser: Borrador, Enviado, Aceptado, Rechazado o Facturado.' },
        ],
      },
      {
        title: 'Seleccionar cliente',
        steps: [
          { icon: <Users size={14} />, text: 'Pulsa el botón "Seleccionar cliente" para buscar en tu base de datos de clientes.' },
          { icon: <Search size={14} />, text: 'Escribe nombre, empresa o NIF en el buscador del selector. Los resultados se filtran en tiempo real.' },
          { icon: <CheckCircle size={14} />, text: 'Al seleccionar un cliente, sus datos (nombre, dirección, email, teléfono) se rellenan automáticamente en el presupuesto.' },
          { icon: <ArrowRight size={14} />, text: 'Si el cliente es nuevo, créalo primero en el módulo "Clientes" y luego vuelve al editor.' },
        ],
      },
      {
        title: 'Datos de siniestro / aseguradora (opcional)',
        steps: [
          { icon: <ArrowRight size={14} />, text: 'Si el trabajo está vinculado a una aseguradora, rellena los campos: Aseguradora, Nº Póliza, Nº Siniestro y Descripción del siniestro.' },
          { icon: <Info size={14} />, text: 'Estos campos son opcionales. Si no aplican, déjalos en blanco y no aparecerán en el PDF.' },
        ],
      },
      {
        title: 'Añadir partidas (líneas del presupuesto)',
        steps: [
          { icon: <PlusCircle size={14} />, text: 'Pulsa "+ Añadir línea" para añadir una partida manual. Rellena descripción, unidades, precio unitario, IVA y descuento.' },
          { icon: <Package size={14} />, text: 'Pulsa "Añadir desde catálogo" para seleccionar un producto/servicio de tu catálogo. El precio, IVA y unidad se rellenan automáticamente.', note: 'Puedes modificar cualquier campo después de seleccionar del catálogo.' },
          { icon: <Edit3 size={14} />, text: 'Edita directamente las celdas de cada línea: descripción, cantidad, precio, % IVA y % descuento.' },
          { icon: <Trash2 size={14} />, text: 'Pulsa el icono de papelera al final de cada fila para eliminar esa partida.' },
          { icon: <RefreshCcw size={14} />, text: 'Los totales (subtotal, descuentos, IVA desglosado, total) se actualizan automáticamente al instante.' },
        ],
      },
      {
        title: 'Notas y condiciones',
        steps: [
          { icon: <ArrowRight size={14} />, text: 'En el campo "Notas" puedes añadir observaciones al cliente (ej: "Precio válido salvo variación de materiales").' },
          { icon: <ArrowRight size={14} />, text: 'En "Condiciones" puedes indicar las condiciones de pago o garantía (ej: "50% a la firma, 50% a la entrega").' },
          { icon: <Info size={14} />, text: 'Ambos campos aparecen al final del PDF si están rellenos.' },
        ],
      },
      {
        title: 'Guardar y visualizar',
        steps: [
          { icon: <CheckCircle size={14} />, text: 'Pulsa "Guardar presupuesto" para guardar el borrador. Los datos se almacenan localmente en tu navegador.' },
          { icon: <Eye size={14} />, text: 'Pulsa "Ver PDF" para previsualizar el presupuesto en formato A4 antes de descargarlo.' },
        ],
      },
    ],
    tips: [
      'Guarda frecuentemente para no perder cambios.',
      'Puedes volver al editor desde el Dashboard en cualquier momento pulsando "Editar" en el menú de acciones del presupuesto.',
    ],
    warnings: [
      'Si cierras el navegador sin guardar, perderás los cambios no guardados.',
    ],
  },
  {
    id: 'pdf',
    icon: <Printer size={20} />,
    title: 'Previsualizar y descargar PDF',
    color: 'text-primary',
    intro: 'La vista previa genera un documento profesional en formato A4 con todos los datos del presupuesto, tu logo, información de empresa y desglose de IVA.',
    steps: [
      { icon: <Eye size={14} />, text: 'Desde el Dashboard o el editor, pulsa "Ver PDF" (icono de ojo o "Vista previa") para abrir la previsualización.' },
      { icon: <Info size={14} />, text: 'Verás el documento completo: cabecera con tu logo y datos de empresa, datos del cliente, tabla de partidas, totales, desglose de IVA por tipo, IBAN y pie de página.' },
      { icon: <Download size={14} />, text: 'Pulsa el botón "Descargar PDF" en la parte superior. Se abrirá la ventana de impresión de tu navegador.' },
      { icon: <Printer size={14} />, text: 'En el diálogo de impresión, selecciona "Guardar como PDF" como destino (o la impresora que desees). Confirma.', note: 'En Chrome: "Destino → Guardar como PDF". En Firefox: "Imprimir a archivo PDF".' },
    ],
    tips: [
      'El color de cabecera del PDF es el color primario configurado en Configuración (por defecto verde oscuro #2E5F5E).',
      'Si tienes tu logo configurado, aparecerá en la esquina superior izquierda del PDF.',
      'El IBAN solo aparece en el PDF si está configurado en Configuración.',
    ],
    warnings: [
      'No cierres la ventana de previsualización mientras se está generando el PDF.',
      'Si el PDF se ve en blanco, asegúrate de que no tienes bloqueadores de ventanas emergentes activos.',
    ],
  },
  {
    id: 'clientes',
    icon: <Users size={20} />,
    title: 'Gestión de clientes',
    color: 'text-primary',
    intro: 'El módulo de Clientes es tu agenda de contactos. Guarda particulares, empresas y aseguradoras para reutilizarlos en tus presupuestos sin tener que volver a escribirlos.',
    steps: [
      { icon: <PlusCircle size={14} />, text: 'Pulsa "+ Nuevo cliente" para añadir un contacto. Rellena nombre, tipo (Particular / Empresa / Aseguradora), NIF, dirección, teléfono y email.' },
      { icon: <Tag size={14} />, text: 'Elige el tipo de cliente: "Particular" para personas físicas, "Empresa" para sociedades mercantiles, "Aseguradora" para compañías de seguros. El tipo se muestra como etiqueta de color.' },
      { icon: <Search size={14} />, text: 'Usa el buscador superior para filtrar clientes por nombre, NIF o tipo en tiempo real.' },
      { icon: <Edit3 size={14} />, text: 'Pulsa el icono de edición (lápiz) sobre una fila para modificar los datos del cliente.' },
      { icon: <Trash2 size={14} />, text: 'Pulsa el icono de papelera para eliminar un cliente. Se te pedirá confirmación antes de borrar.' },
      { icon: <Info size={14} />, text: 'Cada cliente muestra el número de presupuestos que tiene vinculados, para que sepas qué contactos son más activos.' },
    ],
    tips: [
      'Crea primero los clientes más habituales antes de empezar a generar presupuestos.',
      'Para aseguradoras, usa el campo NIF para el CIF de la compañía.',
    ],
    warnings: [
      'Si eliminas un cliente, los presupuestos existentes vinculados a él no se borran, pero perderán la referencia al cliente.',
    ],
  },
  {
    id: 'productos',
    icon: <Package size={20} />,
    title: 'Catálogo de productos y servicios',
    color: 'text-primary',
    intro: 'El catálogo contiene tus tarifas estándar. Incluye 32 partidas precargadas para los sectores de albañilería, fontanería, pintura, carpintería y más. Puedes añadir, editar o desactivar cualquiera.',
    subsections: [
      {
        title: 'Navegar por el catálogo',
        steps: [
          { icon: <Search size={14} />, text: 'Usa el buscador para filtrar por nombre o descripción del producto/servicio.' },
          { icon: <Tag size={14} />, text: 'Usa el selector de categorías para ver solo las partidas de un tipo: Mano de obra, Demolición, Albañilería, Solados, Fontanería, Pintura, Carpintería o Gestión.' },
          { icon: <ToggleRight size={14} />, text: 'El interruptor de cada partida permite activarla o desactivarla. Las partidas inactivas no aparecen al seleccionar desde el editor de presupuestos.' },
        ],
      },
      {
        title: 'Añadir o editar partidas',
        steps: [
          { icon: <PlusCircle size={14} />, text: 'Pulsa "+ Nueva partida" para crear un producto o servicio personalizado. Rellena: nombre, descripción (opcional), categoría, unidad de medida, precio base, % IVA y si está activo.' },
          { icon: <Edit3 size={14} />, text: 'Pulsa el icono de edición para modificar cualquier campo de una partida existente, incluyendo el precio.' },
          { icon: <Trash2 size={14} />, text: 'Elimina partidas que ya no uses. Esta acción es permanente y requiere confirmación.' },
        ],
      },
    ],
    tips: [
      'El precio del catálogo es solo orientativo. Puedes modificarlo en cada línea del presupuesto sin afectar al catálogo.',
      'Usa la opción "Desactivar" en lugar de eliminar para conservar el historial y poder reactivar la partida más adelante.',
      'Las unidades disponibles son: m², ml, ud, h (hora), kg, m³, día, gl (global).',
    ],
  },
  {
    id: 'configuracion',
    icon: <Settings size={20} />,
    title: 'Configuración',
    color: 'text-primary',
    intro: 'En Configuración personalizas todos los datos de tu empresa que aparecerán en los presupuestos PDF: logo, nombre, NIF, dirección, IBAN y colores.',
    subsections: [
      {
        title: 'Datos de empresa',
        steps: [
          { icon: <Edit3 size={14} />, text: 'Rellena: Nombre de empresa, NIF/CIF, Dirección, Código Postal, Ciudad, Teléfono, Email y Web.' },
          { icon: <ArrowRight size={14} />, text: 'Estos datos aparecen en la cabecera de todos los presupuestos PDF.' },
          { icon: <ArrowRight size={14} />, text: 'El campo IBAN se muestra al final del PDF como referencia de pago.' },
        ],
      },
      {
        title: 'Logo de empresa',
        steps: [
          { icon: <Info size={14} />, text: 'El logo de "Reparaciones y Reformas Oscar Alminos" viene preconfigurado por defecto y aparece en todos los PDFs automáticamente.' },
          { icon: <ArrowRight size={14} />, text: 'Si quieres usar otro logo, pulsa el área de imagen o escribe una URL pública de la imagen.' },
          { icon: <RefreshCcw size={14} />, text: 'Si has cambiado el logo y quieres volver al original, pulsa "Restaurar logo por defecto".' },
        ],
      },
      {
        title: 'Apariencia del PDF',
        steps: [
          { icon: <ArrowRight size={14} />, text: 'El campo "Color del PDF" define el color de la cabecera y los elementos de acento del documento. Por defecto: verde oscuro #2E5F5E.' },
          { icon: <ArrowRight size={14} />, text: 'El campo "Pie de página" aparece al final del PDF. Personalízalo con tu slogan o mensaje de agradecimiento.' },
        ],
      },
      {
        title: 'Contraseña de acceso',
        steps: [
          { icon: <Shield size={14} />, text: 'Puedes cambiar la contraseña de acceso desde esta sección. Introduce la nueva contraseña y confirma el cambio.' },
          { icon: <AlertTriangle size={14} />, text: 'Guarda la nueva contraseña en un lugar seguro. Si la pierdes, necesitarás asistencia técnica para recuperar el acceso.', note: 'La contraseña se almacena como hash criptográfico, nunca en texto plano.' },
        ],
      },
    ],
    tips: [
      'Pulsa "Guardar configuración" para que los cambios queden guardados antes de salir.',
      'Puedes previsualizar el color del PDF en tiempo real antes de guardar.',
    ],
  },
  {
    id: 'backup',
    icon: <Database size={20} />,
    title: 'Copia de seguridad y restauración',
    color: 'text-primary',
    intro: 'La aplicación guarda todos los datos en el almacenamiento local de tu navegador. Para no perder los datos al cambiar de equipo o navegador, usa las copias de seguridad.',
    steps: [
      { icon: <Download size={14} />, text: 'Ve a Configuración → sección "Copia de seguridad".' },
      { icon: <Download size={14} />, text: 'Pulsa "Exportar datos" para descargar un archivo JSON con todos tus presupuestos, clientes, productos y configuración. Guárdalo en un lugar seguro (nube, pendrive, email...).' },
      { icon: <ArrowRight size={14} />, text: 'Verás la fecha y hora de la última exportación realizada.' },
      { icon: <RefreshCcw size={14} />, text: 'Para restaurar, pulsa "Importar datos", selecciona el archivo JSON exportado anteriormente y confirma. La página se recargará con todos los datos restaurados.', note: 'La importación reemplaza TODOS los datos actuales. Asegúrate de exportar primero si tienes datos recientes que no quieres perder.' },
    ],
    tips: [
      'Exporta una copia de seguridad al menos una vez a la semana.',
      'Guarda varias versiones del archivo de backup con la fecha en el nombre (ej: backup-2026-05-17.json).',
      'Usa el archivo de backup para transferir los datos a otro ordenador o navegador.',
    ],
    warnings: [
      'Si limpias el historial del navegador o los datos del sitio, todos los datos de la aplicación se borrarán. Realiza siempre una exportación antes de limpiar el navegador.',
      'La importación sobreescribe todos los datos existentes. No hay marcha atrás si no tienes el backup anterior.',
    ],
  },
  {
    id: 'flujo',
    icon: <Star size={20} />,
    title: 'Flujo de trabajo recomendado',
    color: 'text-[#DDA84D]',
    intro: 'Para sacarle el máximo partido a Presupuesto Pro, sigue este flujo de trabajo desde el primer día:',
    steps: [
      { icon: <CheckCircle size={14} />, text: '1. CONFIGURA TU EMPRESA: Ve a Configuración y rellena todos los datos (nombre, NIF, dirección, IBAN, logo, pie de página). Guarda.' },
      { icon: <CheckCircle size={14} />, text: '2. CARGA TUS CLIENTES HABITUALES: Ve a Clientes y añade los contactos con los que trabajas regularmente.' },
      { icon: <CheckCircle size={14} />, text: '3. REVISA EL CATÁLOGO: Ve a Productos y ajusta los precios de las partidas según tus tarifas actuales. Desactiva las que no uses.' },
      { icon: <CheckCircle size={14} />, text: '4. CREA TU PRIMER PRESUPUESTO: Pulsa "Nuevo presupuesto" en el Dashboard, selecciona el cliente, añade partidas desde el catálogo y guarda.' },
      { icon: <CheckCircle size={14} />, text: '5. DESCARGA EL PDF: Desde la vista previa, descarga el PDF y envíaselo al cliente por email o WhatsApp.' },
      { icon: <CheckCircle size={14} />, text: '6. ACTUALIZA EL ESTADO: Cuando el cliente confirme, ve al presupuesto y cambia el estado a "Aceptado".' },
      { icon: <CheckCircle size={14} />, text: '7. EXPORTA UNA COPIA: Al final de la semana, exporta una copia de seguridad desde Configuración.' },
    ],
    tips: [
      'Mantén siempre actualizados los estados de los presupuestos para que los KPIs del Dashboard sean precisos.',
      'Usa la función "Duplicar" para crear presupuestos similares rápidamente, modificando solo las diferencias.',
    ],
  },
];

// ── Componente ────────────────────────────────────────────────────────────────
export default function Manual() {
  const [open, setOpen] = useState<string | null>('inicio');

  return (
    <AppLayout title="Manual de ayuda" active="manual">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">

        {/* Cabecera */}
        <div className="mb-8 flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <BookOpen size={24} className="text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-1">Manual de uso</h1>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Guía completa de <strong className="text-foreground">Presupuesto Pro</strong> — la herramienta de gestión de presupuestos para <strong className="text-foreground">Reparaciones y Reformas Oscar Alminos</strong>.
              Aquí encontrarás todo lo que necesitas para manejar la aplicación con confianza.
            </p>
          </div>
        </div>

        {/* Índice rápido */}
        <div className="mb-6 p-4 bg-muted/40 rounded-xl border border-border">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
            <HelpCircle size={13} /> Contenido del manual
          </p>
          <div className="flex flex-wrap gap-2">
            {SECTIONS.map(s => (
              <button
                key={s.id}
                onClick={() => { setOpen(s.id); setTimeout(() => document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50); }}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-background border border-border hover:border-primary hover:text-primary transition-colors"
              >
                <span className="text-primary">{s.icon}</span>
                {s.title}
              </button>
            ))}
          </div>
        </div>

        {/* Secciones accordion */}
        <div className="space-y-3">
          {SECTIONS.map((section, idx) => (
            <motion.div
              key={section.id}
              id={section.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.04 }}
              className="border border-border rounded-xl bg-card overflow-hidden"
            >
              {/* Cabecera del acordeón */}
              <button
                onClick={() => setOpen(o => o === section.id ? null : section.id)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-muted/30 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <span className={`${section.color}`}>{section.icon}</span>
                  <span className="font-semibold text-sm text-foreground">{section.title}</span>
                </div>
                <span className="text-muted-foreground group-hover:text-foreground transition-colors">
                  {open === section.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </span>
              </button>

              {/* Contenido expandido */}
              <AnimatePresence initial={false}>
                {open === section.id && (
                  <motion.div
                    key="content"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.22, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5 space-y-4 border-t border-border pt-4">
                      {/* Intro */}
                      <p className="text-sm text-muted-foreground leading-relaxed">{section.intro}</p>

                      {/* Pasos directos */}
                      {section.steps && (
                        <StepList steps={section.steps} />
                      )}

                      {/* Subsecciones */}
                      {section.subsections && section.subsections.map((sub, si) => (
                        <div key={si} className="space-y-2">
                          <h4 className="text-xs font-bold uppercase tracking-wider text-primary/70 pt-1">
                            {sub.title}
                          </h4>
                          <StepList steps={sub.steps} />
                        </div>
                      ))}

                      {/* Tips */}
                      {section.tips && section.tips.length > 0 && (
                        <div className="space-y-1.5">
                          {section.tips.map((tip, ti) => (
                            <div key={ti} className="flex items-start gap-2 text-xs text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg px-3 py-2">
                              <CheckCircle size={13} className="flex-shrink-0 mt-0.5" />
                              <span>{tip}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Warnings */}
                      {section.warnings && section.warnings.length > 0 && (
                        <div className="space-y-1.5">
                          {section.warnings.map((w, wi) => (
                            <div key={wi} className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
                              <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                              <span>{w}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-10 text-center text-xs text-muted-foreground">
          <p>
            Presupuesto Pro &mdash; desarrollado por{' '}
            <a href="https://movilizatic.net" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              movilizatic.net
            </a>
          </p>
          <p className="mt-1">Para soporte técnico o mejoras, contacta a través de la web.</p>
        </div>
      </div>
    </AppLayout>
  );
}

// ── Sub-componente pasos ──────────────────────────────────────────────────────
function StepList({ steps }: { steps: Step[] }) {
  return (
    <ul className="space-y-2">
      {steps.map((step, i) => (
        <li key={i} className="flex items-start gap-2.5">
          <span className="flex-shrink-0 mt-0.5 text-primary">{step.icon}</span>
          <div>
            <span className="text-sm text-foreground/90 leading-snug">{step.text}</span>
            {step.note && (
              <p className="mt-1 text-xs text-muted-foreground italic pl-0">{step.note}</p>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
